/**
 * TributeThankYouModal — after a visitor leaves a tribute (candle,
 * rose, prayer …), we surface a small, warm modal offering a
 * downloadable PNG "thank-you" card that credits them for the gesture.
 *
 * The card is rendered client-side on a <canvas> so we don't burn any
 * server or LLM quota — just a keepsake for the visitor to share on
 * social media or WhatsApp.
 *
 * Usage:
 *   const [open, setOpen] = useState(null);   // { name, kind }
 *   after successful POST → setOpen({ ...visitor, kind, memorial })
 *   <TributeThankYouModal open={open} onClose={() => setOpen(null)} />
 */
import { useEffect, useRef, useState } from "react";
import { X, Download, Share2 } from "lucide-react";
import { toast } from "sonner";

const KIND_LABEL = {
  candle:         { verb: "lit a candle for",   emoji: "🕯" },
  rose:           { verb: "left a rose for",     emoji: "🌹" },
  carnation:      { verb: "left a carnation for", emoji: "🌷" },
  cherry_blossom: { verb: "left a cherry blossom for", emoji: "🌸" },
  bouquet:        { verb: "sent a bouquet for", emoji: "💐" },
  sunflower:      { verb: "left a sunflower for", emoji: "🌻" },
  dove:           { verb: "released a dove for", emoji: "🕊" },
  prayer:         { verb: "offered a prayer for", emoji: "🙏" },
  flower:         { verb: "sent flowers for",    emoji: "🌼" },
  heart:          { verb: "left love for",       emoji: "♥" },
};

function drawCard(canvas, { visitorName, kind, memorialName, memorialYears, note }) {
  const meta = KIND_LABEL[kind] || KIND_LABEL.candle;
  const W = 1200, H = 630;
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Warm sunset gradient background
  const grad = ctx.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0.00, "#FEE2C0");
  grad.addColorStop(0.55, "#F9B378");
  grad.addColorStop(1.00, "#8C4A2E");
  ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);

  // Silhouetted pine motif on the right
  ctx.fillStyle = "rgba(31, 22, 12, 0.55)";
  const pines = [
    { x: W - 160, base: H - 40, h: 320 },
    { x: W - 240, base: H - 40, h: 260 },
    { x: W - 90,  base: H - 40, h: 210 },
  ];
  for (const p of pines) {
    ctx.beginPath();
    ctx.moveTo(p.x, p.base - p.h);
    ctx.lineTo(p.x - 60, p.base);
    ctx.lineTo(p.x + 60, p.base);
    ctx.closePath(); ctx.fill();
  }

  // Vignette
  const vg = ctx.createRadialGradient(W/2, H/2, H * 0.3, W/2, H/2, H);
  vg.addColorStop(0, "rgba(0,0,0,0)"); vg.addColorStop(1, "rgba(0,0,0,0.35)");
  ctx.fillStyle = vg; ctx.fillRect(0, 0, W, H);

  // Big emoji at the top-left
  ctx.font = "bold 96px 'Apple Color Emoji','Segoe UI Emoji','Noto Color Emoji',serif";
  ctx.textBaseline = "top"; ctx.fillStyle = "#1F1B14";
  ctx.fillText(meta.emoji, 70, 80);

  // Overline
  ctx.font = "italic 500 30px Georgia, 'Times New Roman', serif";
  ctx.fillStyle = "#3B2A1E";
  ctx.fillText("A moment of remembrance", 70, 200);

  // Line 1: visitor name
  ctx.font = "bold 60px Georgia, serif"; ctx.fillStyle = "#14100A";
  ctx.fillText(visitorName || "You", 70, 250);

  // Line 2: action
  ctx.font = "italic 40px Georgia, serif"; ctx.fillStyle = "#4A3524";
  ctx.fillText(meta.verb, 70, 330);

  // Line 3: memorial
  ctx.font = "bold 48px Georgia, serif"; ctx.fillStyle = "#14100A";
  const memText = memorialYears ? `${memorialName} (${memorialYears})` : memorialName;
  wrapText(ctx, memText || "a life well lived", 70, 385, W - 200, 58);

  // Note (optional)
  if (note) {
    ctx.font = "italic 26px Georgia, serif"; ctx.fillStyle = "#FDF6ED";
    // scrim behind text
    ctx.fillStyle = "rgba(20, 16, 10, 0.35)";
    ctx.fillRect(50, 500, W - 100, 85);
    ctx.fillStyle = "#FDF6ED";
    ctx.font = "italic 26px Georgia, serif";
    wrapText(ctx, `"${note}"`, 70, 520, W - 140, 34);
  } else {
    // Watermark
    ctx.font = "500 22px 'Helvetica Neue', Arial, sans-serif";
    ctx.fillStyle = "rgba(20, 16, 10, 0.6)";
    ctx.fillText("Social Pulse · socialpulse.com/celebrate", 70, 555);
  }
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = String(text || "").split(" ");
  let line = "";
  for (const w of words) {
    const cand = line ? `${line} ${w}` : w;
    if (ctx.measureText(cand).width > maxWidth && line) {
      ctx.fillText(line, x, y); y += lineHeight; line = w;
    } else {
      line = cand;
    }
  }
  if (line) ctx.fillText(line, x, y);
}

export default function TributeThankYouModal({ open, onClose }) {
  const canvasRef = useRef(null);
  const [visitorName, setVisitorName] = useState(open?.visitorName || "");
  const [dataUrl, setDataUrl] = useState("");

  useEffect(() => {
    if (!open) return;
    setVisitorName(open.visitorName || "");
    // Delay so the canvas element is mounted
    setTimeout(() => render(open.visitorName || ""), 30);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const render = (name) => {
    if (!canvasRef.current || !open) return;
    drawCard(canvasRef.current, {
      visitorName: (name || "").trim() || "You",
      kind: open.kind,
      memorialName: open.memorialName,
      memorialYears: open.memorialYears,
      note: open.note,
    });
    setDataUrl(canvasRef.current.toDataURL("image/png"));
  };

  const download = () => {
    if (!dataUrl) return;
    const safeName = (open?.memorialName || "memorial").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `tribute-${safeName}.png`;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success("Tribute card downloaded");
  };

  const shareNative = async () => {
    if (!dataUrl) return;
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "tribute.png", { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "In loving memory", text: `Remembering ${open.memorialName}` });
      } else {
        download();
      }
    } catch { /* user cancelled */ }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-[#14213D]/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="wofy-card w-full max-w-2xl" onClick={(e) => e.stopPropagation()} data-testid="mem-thankyou-modal">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="overline">A KEEPSAKE OF YOUR TRIBUTE</div>
            <h3 className="font-display text-lg mt-0.5">Thank you for remembering {open.memorialName}</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1 opacity-60 hover:opacity-100" data-testid="mem-thankyou-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="mb-3">
          <label className="text-xs font-medium mb-1 block opacity-70">Your name (optional)</label>
          <input
            className="wofy-input"
            value={visitorName}
            onChange={(e) => { setVisitorName(e.target.value); render(e.target.value); }}
            placeholder="e.g. Anita G · A friend from Pala"
            maxLength={80}
            data-testid="mem-thankyou-name"
          />
        </div>
        <div className="mb-4 rounded-xl overflow-hidden border" style={{ borderColor: "var(--wofy-border)" }}>
          <canvas ref={canvasRef} className="w-full h-auto block" style={{ aspectRatio: "1200/630" }} data-testid="mem-thankyou-canvas" />
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" onClick={onClose} className="wofy-btn-ghost text-sm">Skip</button>
          <button type="button" onClick={shareNative} className="wofy-btn-ghost text-sm" data-testid="mem-thankyou-share">
            <Share2 className="w-4 h-4" /> Share
          </button>
          <button type="button" onClick={download} className="wofy-btn-primary text-sm" data-testid="mem-thankyou-download">
            <Download className="w-4 h-4" /> Download card
          </button>
        </div>
      </div>
    </div>
  );
}
